import { Font, Only, Properties, useTestIds } from "@homebound/beam";
import { format } from "date-fns";
import { emptyCellDash as mdashEl } from "src/components";
import { Temporal } from "temporal-polyfill";

/**
 * Date format styles.
 *
 * none: Specifies no style, i.e. do not display.
 * short: Specifies a short style, typically numeric only, such as “11/23/37” or “3:30 PM”.
 * medium: Specifies a medium style, typically with abbreviated text, such as “Nov. 23, 1937” or “3:30:32 PM”.
 * long: Specifies a long style, such as “November 3, 1937” or “3:30:32 PM”.
 * monthShort: Specifies a short style with month and day, such as “Nov 23”.
 * weekDayMonthShort: Specifies a short style with weekday, month, and day, such as “Tue, Nov 23”.
 */
export const formattedDateStyles = ["none", "short", "medium", "long", "monthShort", "weekDayMonthShort"] as const;
export type FormattedDateStyle = (typeof formattedDateStyles)[number];

export type FormattedDateProps<X> = {
  id?: string;
  xss?: X;
  date: Temporal.PlainDate | Temporal.ZonedDateTime | undefined;
  dateFormatStyle?: FormattedDateStyle;
  timeFormatStyle?: FormattedDateStyle;
  mdash?: boolean;
};

type FormattedDateXss = Pick<Properties, Font | "color">;

const dateStyleFormats: Record<FormattedDateStyle, string> = {
  none: "",
  short: "MM/dd/yy",
  medium: "MMM. dd, yyyy",
  long: "MMMM d, yyyy",
  monthShort: "MMM do",
  weekDayMonthShort: "EEE, MMM do",
};

const timeStyleFormats: Record<FormattedDateStyle, string> = {
  none: "",
  short: "hh:mm aaa",
  medium: "hh:mm:ss aaa",
  long: "hh:mm:ss aaa",
  monthShort: "hh:mm aaa",
  weekDayMonthShort: "hh:mm aaa",
};

/**
 * Formats a date with standard date/time styles and return a component.
 *
 * examples:
 *
 *  dateFormatStyle=short, timeFormatStyle=none (default) -> 11/01/20
 *
 *  dateFormatStyle=short, timeFormatStyle=short -> 11/01/20 03:30 pm
 *
 *  dateFormatStyle=none,  timeFormatStyle=short -> 03:30 pm
 *
 *  dateFormatStyle=monthShort, timeFormatStyle=none -> Nov 1st
 *
 *  dateFormatStyle=monthShort, timeFormatStyle=short -> Nov 1st 03:30 pm
 */
export function FormattedDate<X extends Only<FormattedDateXss, X>>(props: FormattedDateProps<X>) {
  const { xss, date, dateFormatStyle = "short", timeFormatStyle = "none", id = "FormattedDate", mdash } = props;

  const testIds = useTestIds({}, id);
  const formattedDate = formatDate(date, dateFormatStyle, timeFormatStyle);

  // TODO: figure out what to do here w/ date/time format. It seems like the intention
  // was to provide more detail in the hover? so instead of a 2 digit year a 4 digit year
  // maybe we should just bump up to next level of detail for date/time if specified?...
  const formattedHoverDate = date ? formatDate(date, "medium") : "";
  const emptyContent = mdash ? mdashEl : "";
  const content = date === undefined ? emptyContent : formattedDate;

  return (
    <div css={xss} {...testIds} title={date ? formattedHoverDate : undefined}>
      {content}
    </div>
  );
}

/** Formats a date with standard date/time styles and return a string */
export function formatDate(
  rawDate: Temporal.PlainDate | Temporal.ZonedDateTime | undefined | null,
  dateFormatStyle: FormattedDateStyle = "short",
  timeFormatStyle: FormattedDateStyle = "none",
): string | undefined {
  if (rawDate) {
    const formatStr =
      [dateStyleFormats[dateFormatStyle], timeStyleFormats[timeFormatStyle]].filter((f) => f.length).join(" ") ||
      dateStyleFormats.short;
    return format(toDisplayDate(rawDate), formatStr);
  }
}

function toDisplayDate(date: Temporal.PlainDate | Temporal.ZonedDateTime): Date {
  if (date instanceof Temporal.ZonedDateTime) return date.toLegacyDate();
  /**
   * PlainDate has no time zone, so for display formatting we preserve the legacy "local
   * midnight" behavior.
   *
   * I.e. `2018-01-05` with `timeFormatStyle="short"` should display `01/05/18 12:00 am`.
   * If we first anchor that PlainDate in PT, the same value can render as `08:00 am` in a
   * UTC environment, which is surprising for a date-only value.
   */
  return new Date(date.year, date.month - 1, date.day);
}
